/**
 * Подписи версий договорной сметы для списков выбора (ЗВИ, сверка КС Ф3,
 * пакет КС) и таблицы версий: номер, формат, статус по-русски, число позиций,
 * сумма и дата загрузки. Без числа позиций версия на 1 позицию и версия на 722
 * выглядели в списке одинаково («v3 · lsr · активная»).
 */

/** Поля версии, из которых собирается подпись (есть и у версии, и у варианта выбора). */
export interface EstimateVersionFields {
  version_no: number;
  /** active | superseded */
  status: string;
  /** lsr | rdc | vor | manual */
  source: string;
  item_count: number;
  base_total: number | null;
  created_at: string;
}

/** Код ответа «нужно подтверждение» загрузки (бэкенд, `confirmation_required`). */
export const CONFIRMATION_REQUIRED = 'confirmation_required';

export function estimateStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'действующая';
    case 'superseded':
      return 'в архиве';
    default:
      return status;
  }
}

export function estimateSourceLabel(source: string): string {
  switch (source) {
    case 'lsr':
      return 'ЛСР';
    case 'rdc':
      return 'РДЦ';
    case 'vor':
      return 'ВОР';
    case 'manual':
      return 'вручную';
    default:
      return source;
  }
}

/** «1 позиция», «722 позиции», «5 позиций». */
export function positionsLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = 'позиций';
  if (mod100 < 11 || mod100 > 14) {
    if (mod10 === 1) word = 'позиция';
    else if (mod10 >= 2 && mod10 <= 4) word = 'позиции';
  }
  return `${n} ${word}`;
}

const MONEY = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function estimateTotalLabel(total: number | null): string {
  return total == null ? 'без суммы' : `${MONEY.format(total)} ₽`;
}

export function estimateDateLabel(createdAt: string): string {
  const d = new Date(createdAt);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU');
}

/** «v3 · ЛСР · действующая · 1 позиция · 1 234 ₽ · от 23.07.2026». */
export function estimateVersionLabel(v: EstimateVersionFields): string {
  const parts = [
    `v${v.version_no}`,
    estimateSourceLabel(v.source),
    estimateStatusLabel(v.status),
    positionsLabel(v.item_count),
    estimateTotalLabel(v.base_total),
  ];
  const date = estimateDateLabel(v.created_at);
  if (date) parts.push(`от ${date}`);
  return parts.join(' · ');
}

/** «v1, v2, v3» — номера версий по возрастанию. */
export function versionList(versions: { version_no: number }[]): string {
  return [...versions]
    .map((v) => v.version_no)
    .sort((a, b) => a - b)
    .map((n) => `v${n}`)
    .join(', ');
}
